import { useAuth } from '@/lib/auth/context';
import { isPlatformRole, isMerchantRole } from '@/types/auth';

/**
 * Result of {@link useMerchantScope}.
 */
export interface MerchantScope {
  /** True only for platform roles — they see and select the merchant. */
  showMerchant: boolean;
  /** A merchant role's own tenant id; null for platform roles or when unset. */
  ownMerchantId: string | null;
}

/**
 * Single source of truth for merchant-context visibility (Spec 0220).
 *
 * Pure derivation from {@link useAuth} — no fetch, no side effects. A merchant
 * login is already tenant-bound via `AuthUser.merchantId`, so the UI must not
 * ask merchant roles to pick or display their own merchant. Centralising the
 * check here replaces scattered `role`/`hasRole(PLATFORM)` tests and prevents
 * the drift seen between the filter components.
 */
export function useMerchantScope(): MerchantScope {
  const { user } = useAuth();
  const showMerchant = !!user && isPlatformRole(user.role);
  const ownMerchantId = user && isMerchantRole(user.role) ? user.merchantId : null;
  return { showMerchant, ownMerchantId };
}
